<template>
  <div class="-my-2">
    <div v-if="loading || !_reservations.length" class="w-full p-2 text-center">
      <p class="text-sm text-blue-700 dark:text-blue-300">
        {{ loading ? $t('components.recurrence.elements.customer.loading') : $t('reservation.noUpcoming') }}
      </p>
    </div>

    <div v-if="!loading && _reservations.length">
      <div class="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 py-2 flex items-center border-b">
        <div class="pr-3">
          <checkbox-field
            v-model="selectAllChecked"
            :indeterminate="isSelectAllIndeterminate"
            active-class=""
            @change="handleSelectAll"
          />
        </div>
        <div class="mr-3">
          <p class="text-gray-700 text-sm dark:text-gray-200">
            {{ $t('general.selectAll') }}
          </p>
        </div>
      </div>

      <template v-for="item in _reservations" :key="item.reservation.idReservation">
        <div class="py-2 flex items-center border-b last:border-none">
          <div class="pr-3">
            <checkbox-field
              v-model="item.action"
              :value="RecurrenceCustomerAction.book"
              :unchecked-value="RecurrenceCustomerAction.cancel"
              active-class=""
            />
          </div>

          <div class="mr-3">
            <p class="text-gray-700 text-sm dark:text-gray-200">
              {{ item.reservation.infoLabel }}
            </p>
            <p v-if="!!item.reservationCustomer" class="text-green-500 text-xs">
              {{ $t('components.recurrence.elements.customer.alreadyAttending') }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ODescriptionDetails,
} from '@/components/elements'

import {
  ToggleField,
  CheckboxField,
} from '@/components/fields'

import { RecurrenceCustomerAction } from '@/_api/models/recurrence'

const { $api, $date, $notification } = useNuxtApp()

interface Props {
  reservations: any[]
  reservation?: Record<string, any> | null
  customer?: Record<string, any> | null
  editable?: boolean
  auto?: boolean
}

interface Emits {
  (e: 'update:reservations', value: any[]): void
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
  customer: null,
  editable: true,
  auto: false,
})

const emit = defineEmits<Emits>()

const loading = ref(false)
const selectAllChecked = ref(false)

const _reservations = computed({
  get () {
    return props.reservations
  },
  set (value) {
    emit('update:reservations', value)
  },
})

const isSelectAllIndeterminate = computed(() => {
  const selectedCount = _reservations.value.filter((item: any) => item.action === RecurrenceCustomerAction.book).length
  return selectedCount > 0 && selectedCount < _reservations.value.length
})

watch(() => props.reservation, (slot) => {
  if (slot) {
    fetchCustomerRecurrencePreview()
  }
})

watch(_reservations, (newVal) => {
  selectAllChecked.value = newVal.every((item: any) => item.action === RecurrenceCustomerAction.book)
}, { deep: true })

async function fetchCustomerRecurrencePreview () {
  loading.value = true

  const reservation = props.reservation
  const customer = props.customer

  try {
    const date = Number($date.format(new Date()))
    const customerObjectId = customer?.objectID?.() ?? customer?.idCustomer
    const customerId = customerObjectId == null ? undefined : Number(customerObjectId)
    const response = await $api.reservation.previewCustomerRecurrence(
      reservation!.objectID?.() ?? reservation!.idReservation?.toString(),
      { customerId, date }
    )
    const preview = response.data

    _reservations.value = Object.values(preview)
      .filter((reservationRecurrence: any) => {
        return reservationRecurrence.reservation.date
      })
      .map((reservationRecurrence: any) => {
        let action = RecurrenceCustomerAction.cancel

        if (reservationRecurrence.reservationCustomer || (props.auto && reservationRecurrence.remainingSpaces > 0)) {
          action = RecurrenceCustomerAction.book
        }

        return { ...reservationRecurrence, action }
      })
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

function handleSelectAll (checked: boolean) {
  _reservations.value = _reservations.value.map((reservation: any) => ({
    ...reservation,
    action: checked ? RecurrenceCustomerAction.book : RecurrenceCustomerAction.cancel,
  }))
}

onMounted(() => {
  fetchCustomerRecurrencePreview()
})
</script>
